import React, { useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';

import {
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
} from '@fluentui/react';

import { SignupUserInfoForm } from './signup-user-info-form';
import type {
  SignupUserInfo,
  SignupUserInfoDialogProps,
  SignupUserInfoValidationResult,
} from './types';

export const getStyles = () => ({
  contentList: {
    marginTop: 10.5,
  },
});

export function SignupUserInfoDialog(props: SignupUserInfoDialogProps) {
  const {
    onConfirm,
    defaultValue,
  } = props;
  const i18n = useContext(I18nContext);
  const [formData, setFormData] = useState<SignupUserInfo | undefined>(defaultValue);
  const [isValid, setIsValid] = useState(false);
  const [hideDialog, setHideDialog] = useState(false);
  const dialogContentProps = {
    type: DialogType.normal,
    title: i18n.getString(_TL_("Before we can create your account, we'll need a little more information first.")),
    showCloseButton: false,
  };

  const onValidate = useCallback((result: SignupUserInfoValidationResult) => {
    setFormData(result.data);
    setIsValid(result.isValid);
  }, []);

  const onDismissDialog = useCallback(() => setHideDialog(true), []);
  const dialogSizeProps = {
    minHeight: 204,
    maxHeight: 204,
  };

  return (
    <Dialog
      className="signup-user-info-dialog"
      hidden={hideDialog}
      onDismiss={onDismissDialog}
      modalProps={{ isBlocking: true }}
      dialogContentProps={dialogContentProps}
      maxWidth={560}
      minWidth={560}
      {...dialogSizeProps}
    >
      <SignupUserInfoForm
        {...props}
        onValidate={onValidate}
      />
      <DialogFooter>
        <PrimaryButton
          onClick={() => {
            onConfirm(formData!);
            setHideDialog(true);
          }}
          text={i18n.getString(_TL_('Continue'))}
          disabled={!isValid}
        />
      </DialogFooter>
    </Dialog>
  );
}

SignupUserInfoDialog.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  defaultValue: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  modalProps: PropTypes.shape({}),
};

SignupUserInfoDialog.defaultProps = {
  defaultValue: undefined,
  modalProps: {},
};
